/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { AnalyticEvents } from '@constants/index';
import { Analytics } from '@services/analytics-service';
import clsx from 'clsx';
import Button from '@carvana/core-helios-button';
import styles from './upload.module.css';
import photoSvg from '../../../../assets/upload-photo.svg';
import ErrorFilled from '../../../../assets/error-filled.svg';
import { useUserSelections } from '../../providers/user-selections';
import { getErrorMessage } from './get-error-message';
import globalStyles from '../../service-and-repair.module.css';
import { SubCategory } from '../../constants';

type FileInput = Partial<{
  lastModified: number;
  src: string;
  lastModifiedDate: string;
  name: string;
  size: number;
  type: string;
}>;

const MAX_CHARS = 3000;

export const ImageUpload = ({ handleNextRoute, showDetailsField = false }: any) => {
  const [photos, setPhotos] = useState<{ close: FileInput; distant: FileInput }>({ close: {}, distant: {} });
  const { hasUploadError, setHasUploadError, userSelections, updateUserSelections } = useUserSelections();
  const [details, setDetails] = useState(userSelections.details || '');
  const [showRequiredError, setShowRequiredError] = useState(false);
  const hasCloseUpAndDistantPhotos = photos && photos.close.src && photos.distant.src;
  const hasValidDetails = showDetailsField ? details.trim().length > 0 : true;
  const canProceed = hasCloseUpAndDistantPhotos && hasValidDetails;
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hasTrackedFocus = useRef(false);

  const isGlassFlow = userSelections.subcategory === SubCategory.Glass;

  useEffect(() => {
    const scrollToTop = () => {
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
      
      const modalContent = document.getElementById('modal-content');
      if (modalContent) {
        modalContent.scrollTop = 0;
      }
      
      const modalBody = document.querySelector('.modal-body');
      if (modalBody) {
        modalBody.scrollTop = 0;
      }
    };

    requestAnimationFrame(() => {
      scrollToTop();
      setTimeout(scrollToTop, 100);
    });
  }, []);
  const disclaimer = isGlassFlow
    ? "Imperfections within Carvana standards and included on the vehicle details page are not covered."
    : "Cosmetic issues within Carvana standards and included on the vehicle's detail page are not covered.";

  const setCloseUpPhoto = (file) => {
    setPhotos({
      ...photos,
      close: file,
    });
  };
  const setDistantPhoto = (file) => {
    setPhotos({
      ...photos,
      distant: file,
    });
  };

  const clearUploadError = () => {
    setHasUploadError(false);
  };

  const handleDetailsFocus = () => {
    if (!hasTrackedFocus.current) {
      hasTrackedFocus.current = true;
      datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsTextboxFocused, { flow: 'cosmetic' });
    }
  };

  const handleDetailsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;

    if (newValue.length <= MAX_CHARS) {
      setDetails(newValue);
      setShowRequiredError(false);
    }
  };

  const onNextClick = () => {
    if (showDetailsField && details.trim().length === 0) {
      setShowRequiredError(true);
      textareaRef.current?.focus();
      return;
    }

    if (showDetailsField) {
      updateUserSelections({ details });
      Analytics.trackEvent(AnalyticEvents.vehicleConcernDetailsCosmeticSubmitted, {
        detailsLength: details.length,
      });
      datadogRum.addAction(AnalyticEvents.vehicleConcernDetailsCosmeticSubmitted, {
        detailsLength: details.length,
      });
    }

    handleNextRoute();
    Analytics.trackEvent(AnalyticEvents.sarCosmeticClickedSaveAndContinue);
    datadogRum.addAction(AnalyticEvents.sarCosmeticClickedSaveAndContinue);
  };

  return (
    <div ref={containerRef} className={globalStyles['page-wrapper']} onClick={clearUploadError}>
      {hasUploadError && <PhotoError error="There was an issue submitting your photos. Please try again." isBanner />}

      <div className={styles['image-upload-header-text-container']}>
        <div>Upload photos</div>
        <p>{disclaimer}</p>
      </div>

      <div className={styles[showDetailsField ? 'image-upload-container-compact' : 'image-upload-container']}>
        <div className={styles['image-upload-section']}>
          <div className={styles['image-upload-photo-upload']}>
            <div>Close-up photo</div>
            <p>
              Take a photo <b>1-2 feet away</b> from the issue.
            </p>
          </div>

          <PhotoUpload alt="close" cb={setCloseUpPhoto} />
        </div>

        <div className={styles['image-upload-section']}>
          <div className={styles['image-upload-photo-upload']}>
            <div>Distant photo</div>
            <p>
              Take a photo <b>4-6 feet away</b> from the issue.
            </p>
          </div>

          <PhotoUpload alt="distant" cb={setDistantPhoto} />
        </div>

        {showDetailsField && (
        <div className={styles['details-section']}>
          <div className={styles['details-header']}>
            <div className={styles['details-label']}>Tell us more about the issue</div>
            <p className={styles['details-help-text']}>
              Add any notes to help our team understand exactly what to look for in the photos.
            </p>
          </div>
          <textarea
            ref={textareaRef}
            rows={4}
            value={details}
            onChange={handleDetailsChange}
            onFocus={handleDetailsFocus}
            className={styles['details-textarea']}
            placeholder=""
            aria-label="Describe the cosmetic issue"
            aria-required="true"
            aria-invalid={showRequiredError}
            aria-describedby={showRequiredError ? 'details-error' : undefined}
            maxLength={MAX_CHARS}
          />
          {showRequiredError && (
            <div className={styles['details-footer']}>
              <span id="details-error" className={styles['details-error']} role="alert" aria-live="polite">
                This field is required
              </span>
            </div>
          )}
        </div>
        )}
      </div>

      <div className={globalStyles['button-wrapper']}>
        <Button type="button" disabled={!canProceed} onClick={onNextClick}>
          Next
        </Button>
      </div>
    </div>
  );
};

const PhotoUpload = ({ alt, cb }: { alt: string; cb: Function }) => {
  const [file, setFile] = useState(photoSvg);
  const [error, setError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { updateUserSelections, userSelections } = useUserSelections();

  const handleChange = (e) => {
    const ddAction = 'sar-image-upload-file-validation';

    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const selectedFileMetadata = {
        size: selectedFile.size,
        type: selectedFile.type,
        name: selectedFile.name,
      };

      const errorMessage = getErrorMessage(selectedFile);

      if (errorMessage) {
        setError(errorMessage);
        datadogRum.addAction(ddAction, {
          type: 'error',
          error: errorMessage,
          fileData: selectedFileMetadata,
        });
        return;
      }

      const src = URL.createObjectURL(selectedFile);
      setFile(src);
      const ind = alt === 'close' ? 0 : 1;
      const { media = [] } = userSelections || {};
      const updatedMedia = [media[0] || {}, media[1] || {}];
      updatedMedia.splice(ind, 1, selectedFile);

      updateUserSelections({ media: updatedMedia });
      cb({ ...selectedFile, src });
    }
  };

  const handleClick = (from: 'button' | 'image square') => {
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }

    if (from === 'image square') {
      Analytics.trackEvent(AnalyticEvents.sarCosmeticClickedImageSquare);
      datadogRum.addAction(AnalyticEvents.sarCosmeticClickedImageSquare);
    } else if (from === 'button') {
      if (uploadButtonText === 'Re-upload') {
        Analytics.trackEvent(AnalyticEvents.sarCosmeticClickReUpload);
        datadogRum.addAction(AnalyticEvents.sarCosmeticClickReUpload);
      } else {
        Analytics.trackEvent(AnalyticEvents.sarCosmeticClickedUpload);
        datadogRum.addAction(AnalyticEvents.sarCosmeticClickedUpload);
      }
    }
  };

  const isSvg = file === photoSvg;
  let uploadButtonText = 'Upload';
  let showReUploadButton = false;
  if (!isSvg && file) {
    uploadButtonText = 'Re-upload';
    showReUploadButton = true;
  }

  return (
    <div className={styles['photo-upload']}>
      {error && <PhotoError error={error} />}

      <div className={styles['photo-upload-container']}>
        <div className={styles['photo-upload-image-container']} onClick={() => handleClick('image square')}>
          <img src={file} alt={alt} className={clsx(styles['photo-upload-img'], isSvg && styles['svg-placeholder'])} />
        </div>
        <input type="file" onChange={handleChange} ref={fileInputRef} style={{ display: 'none' }} accept="image/*" />

        <Button
          priority="secondary"
          type="button"
          onClick={() => handleClick('button')}
          className={clsx(styles['photo-upload-button'], showReUploadButton && styles['re-upload'])}
        >
          {uploadButtonText}
        </Button>
      </div>
    </div>
  );
};

export const PhotoError = ({ error, isBanner }: { error: string; isBanner?: boolean }) => (
  <div className={clsx(styles['photo-error'], isBanner && styles['photo-banner'])}>
    <img src={ErrorFilled} alt="error" />
    <div className={styles['photo-error-text']}>{error}</div>
  </div>
);
